import React, { useEffect, useState } from "react";
import PaymentDialog from "./PaymentDialog";
import { CloseOutlined } from "@mui/icons-material";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { PaymentDetails } from "../../models/PaymentDetails";
import API from "../../services";
import { RootState } from "../../models/RootState";
import { connect } from "react-redux";
import { UserDetails } from "../../models/UserDetails";
import "./DepositForm.scss";
import DownArrow from '../../assets/images/down-arrow.svg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QRCode from "react-qr-code";
import { SiteConfig } from "../../models/SiteConfig";
import { demoUser } from "../../utils/StringUtils";

interface PGItem {
  accountId: string;
  tgProviderId: string;
  category: string;
  pgName?: string;
  minDeposit: number;
  maxDeposit: number;
  minWithdrawal: number;
  maxWithdrawal: number;
  applicationName: string;
}

interface DepositFormProps {
  activeTab: string;
  action: string;
  userDetails: UserDetails;
  pgList: PGItem[];
  siteConfig: SiteConfig;
}

const DepositForm: React.FC<DepositFormProps> = (props) => {
  const { activeTab, userDetails, pgList, siteConfig } = props;
  const isDemo = demoUser();
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentProps, setPaymentProps] = useState<PaymentDetails | null>(null);

  // Helper function to check if user is created from back office
  // Format: {botId}-a... (e.g., 1844-anm777) vs regular: {botId}-{number}... (e.g., 1844-916281755762)
  const isBackOfficeUser = () => {
    const userKey = userDetails?.UserKey || sessionStorage.getItem("userKey");
    if (!userKey) return false;
    
    const parts = userKey.split("-");
    if (parts.length < 2) return false;
    
    // Check if the part after the dash starts with lowercase 'a'
    const afterDash = parts[1];
    return afterDash && /^a/.test(afterDash);
  };

  const isDisabled = loading || amount === undefined || amount <= 0 || (!isBackOfficeUser() && userDetails?.MobileNumber === "" && !siteConfig?.isContactOptional);
  const userKey = new URLSearchParams(window.location.search).get("id");
  const [activeCurType, setActiveCurType] = useState<string>('Fiat');
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [currList, setCurrList] = useState<any>([]);
  const [walletAddress, setWalletAddress] = useState<any>("");
  const [selectedPG, setSelectedPG] = useState<PGItem | null>(null);
  const [fiatPGList, setFiatPGList] = useState<PGItem[]>([]);
  const [cryptoPGList, setCryptoPGList] = useState<PGItem[]>([]);

  // Filter PG list based on active tab/category and set defaults
  useEffect(() => {
    if (isDemo) return;
    const fiatList = pgList?.filter((pg: PGItem) => pg.category.toLowerCase() === 'fiat') ?? [];
    const cryptoList = pgList?.filter((pg: PGItem) => pg.category.toLowerCase() === 'crypto') ?? [];

    setFiatPGList(fiatList);
    setCryptoPGList(cryptoList);

    // Ensure active tab is one that has items
    if (activeCurType === 'Fiat' && fiatList.length === 0 && cryptoList.length > 0) {
      setActiveCurType('Crypto');
      setSelectedPG(cryptoList[0] ?? null);
      return;
    }
    if (activeCurType === 'Crypto' && cryptoList.length === 0 && fiatList.length > 0) {
      setActiveCurType('Fiat');
      setSelectedPG(fiatList[0] ?? null);
      return;
    }

    // Set default selected PG based on current active tab
    if (activeCurType === 'Fiat') {
      setSelectedPG(fiatList.length > 0 ? fiatList[0] : null);
    } else if (activeCurType === 'Crypto') {
      setSelectedPG(cryptoList.length > 0 ? cryptoList[0] : null);
    }
  }, [pgList, activeCurType]);

  // Call getCurrList when selectedPG changes and we're in Crypto tab
  useEffect(() => {
    if (isDemo) return;
    if (activeCurType === "Crypto" && selectedPG && cryptoPGList?.length > 0) {
      // Only call getCurrList if selectedPG is part of cryptoPGList
      const isCryptoPG = cryptoPGList.some(pg => pg.tgProviderId === selectedPG.tgProviderId);
      if (isCryptoPG) {
        getCurrList();
      }
    }
  }, [selectedPG, activeCurType]);

  const closeDialog = () => {
    setShowPayment(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isDemo) return;

    if (amount === undefined) return; // Ensure amount is provided

    setLoading(true);
    try {
      const response = await API.post(`/account/initiateDeposit`, {
        uKey: sessionStorage.getItem("userKey"),
        amount: amount,
        currency: "INR",
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
      });

      const data = response.data;
      if (data.status !== "RS_OK") {
        toast.error(`${data.errorDescription}`);
        return;
      }

      const paymentOptions: PaymentDetails = {
        referenceId: data.data.reference_id,
        bankId: "",
        qrCode: "",
        upiIntent: "",
        isUpi: false,
        actId: "",
        actName: "",
        ifsc: "",
        bankName: "",
        isBankTransfer: false,
        upiId: "",
      };

      data.data.payment_options.forEach((option: any) => {
        if (option.payment_method === "UPI") {
          paymentOptions.bankId = option.payment_method_details.bank_id;
          paymentOptions.qrCode = option.payment_method_details.upi_qr;
          paymentOptions.upiIntent =
            option.payment_method_details.upi_intent.replace("upi://", "");
          paymentOptions.isUpi = true;
          paymentOptions.upiId = option?.payment_method_details?.upi_id;
        } else if (option.payment_method === "BANK_TRANSFER") {
          paymentOptions.actId =
            option.payment_method_details.bank_account_number;
          paymentOptions.actName =
            option.payment_method_details.bank_account_holder;
          paymentOptions.ifsc = option.payment_method_details.ifs_code;
          paymentOptions.bankName = option.payment_method_details.bank_name;
          paymentOptions.bankId = option.payment_method_details.bank_id;
          paymentOptions.isBankTransfer = true;
        }
      });

      setPaymentProps(paymentOptions);
      setShowPayment(true);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrList = async () => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`account/get-crypto-currencies`, {
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
      });
    } catch (e) {
      return;
    }
    if (response && response.data?.currenciesList) {
      setCurrList(response?.data?.currenciesList)
    }
  }

  const getWalletAddress = async (curr: any) => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`account/get-wallet-address`, {
        tgProviderId: selectedPG?.tgProviderId,
        currency: curr?.cryptoCurrency,
        networkId: curr?.networkId,
        accountId: selectedPG?.accountId,
      });
    } catch (e) {
      return;
    }
    if (response?.data?.status !== "RS_OK") {
      toast?.error(response?.data?.errorDescription)
    }

    if (response) {
      setWalletAddress(response?.data?.walletAddress)
    }
  }

  const handleTabClick = (tab: any) => {
    if (isDemo) return;
    // Prevent switching to a tab with no items
    if (tab === 'Fiat' && fiatPGList.length === 0) return;
    if (tab === 'Crypto' && cryptoPGList.length === 0) return;

    setActiveCurType(tab);
    if (tab === 'Fiat') {
      setSelectedPG(fiatPGList[0] ?? null);
    } else {
      setSelectedPG(cryptoPGList[0] ?? null);
      setLoading(false);
    }
  };

  const copyToClipboard = (text: any) => {
    navigator?.clipboard?.writeText(text)?.then(() => {
    });
  }

  const handleCurrencySelect = (curr: any) => {
    if (isDemo) return;
    setSelectedCurrency(curr);
    getWalletAddress(curr)
    setIsOpen(false);
  };
  const toggleDropdown = () => setIsOpen(!isOpen);


  const tabs = [
    {
      label: "Fiat",
      content: (
        <>
          {/* <DepositForm activeTab="Deposit" action="fiat" ></DepositForm> */}
          {!isBackOfficeUser() && (!userDetails?.MobileNumber && !siteConfig?.isContactOptional) && (
            <div className="text-lg mb-1 mb-4">
              Please share Contact before Trying to Deposit the Amount.
            </div>
          )}
          {!showPayment && (isBackOfficeUser() || userDetails?.MobileNumber || siteConfig?.isContactOptional) && (
            <form className="flex flex-col w-full" onSubmit={handleSubmit}>
              <label htmlFor="amount" className="block mt-3 text-sm text-zinc-400">
                Amount
              </label>
              <input
                type="number"
                id="amount"
                className="px-4 py-3 mt-2.5 w-full text-sm font-medium text-white placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem]"
                placeholder="Enter your amount"
                value={amount || ""}
                min={userDetails?.IsPremium ? undefined : selectedPG?.minDeposit}
                max={userDetails?.IsPremium ? undefined : selectedPG?.maxDeposit}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setAmount(value);
                }}
              />
              {amount != null && !userDetails?.IsPremium && ((amount < (selectedPG?.minDeposit || 0)) || amount > (selectedPG?.maxDeposit || 0)) && (
                <span className="mt-1 text-sm text-orange-500">
                  Amount must be between {selectedPG?.minDeposit || 0} and {selectedPG?.maxDeposit || 0}.
                </span>
              )}
              <button
                type="submit"
                className={`p-2.5 mt-4 w-full text-sm text-white whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${isDisabled ? "opacity-50" : ""
                  }`}
                disabled={isDisabled || (!userDetails?.IsPremium && (amount < (selectedPG?.minDeposit || 0) || amount > (selectedPG?.maxDeposit || 0)))}
              >
                {(isBackOfficeUser() || userDetails?.MobileNumber || siteConfig?.isContactOptional) ? (loading ? "Processing..." : "Submit") : "Add Mobile Number"}
              </button>
            </form>
          )
          }

          {showPayment && paymentProps && (
            <PaymentDialog
              paymentProps={paymentProps}
              closeDialog={closeDialog}
              selectedPG={selectedPG}
            />
          )}
        </>
      )
    },
    {
      label: "Crypto",
      content: (
        <>
          {
            cryptoPGList.length === 0 && activeCurType === "Crypto" ? (
              <div className="text-lg mb-1 mb-4">
                Crypto Gateway is not available for this account.
              </div>
            ) : (
              <>
                {
                  currList.length === 0 ? (
                    <div className="text-lg m-2 text-center">
                      No Crypto Currency found.
                    </div>
                  ) : (
                    <>
                      <div className="crypto mt-2">
                        <div className="flex justify-between">
                          <div className="text-border-color text-lg">select currency</div>
                          <div className="relative w-1/2 h-[30px]">
                            <button
                              onClick={toggleDropdown}
                              className="flex items-center h-full justify-between px-3 py-2 w-full text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-slate-800"
                            >
                              <span className="truncate">
                                {selectedCurrency?.cryptoCurrency
                                  ? `${selectedCurrency?.cryptoCurrency} (${selectedCurrency?.blockChain})`
                                  : ""}
                              </span>
                              <img
                                loading="lazy"
                                src={DownArrow}
                                alt=""
                                className="shrink-0 ml-2 w-2.5 aspect-[1.67]"
                              />
                            </button>

                            {isOpen && (
                              <ul className="absolute z-10 w-full left-0 top-full mt-1 bg-inputbox-color border border-slate-800 rounded-lg shadow-lg">
                                {currList?.map((curr: any) => (
                                  <li
                                    key={curr}
                                    onClick={() => handleCurrencySelect(curr)}
                                    className="px-3 py-2 text-xs text-white hover:bg-zinc-700 cursor-pointer"
                                  >
                                    {`${curr?.cryptoCurrency} (${curr?.blockChain})`}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                        </div>
                        {
                          walletAddress ? (
                            <>
                              {<label className="block mt-3 text-sm text-zinc-400">Note: Minimum deposit should be: {selectedPG?.minDeposit || 0 / selectedCurrency?.rate}</label>}
                              <label htmlFor="wallet" className="block mt-1 text-sm text-zinc-400">
                                Your {selectedCurrency?.cryptoCurrency} Deposit Address
                              </label>
                              <div
                                id="wallet"
                                className="flex items-center justify-between px-4 py-3 mt-2.5 w-full text-sm font-medium text-white placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem]"
                              >
                                <span className="truncate text-xs">{walletAddress}</span>
                                <ContentCopyIcon
                                  className="cursor-pointer hover:text-gray-800 ml-2"
                                  onClick={() => copyToClipboard(walletAddress)}
                                  titleAccess="Copy to clipboard"
                                />
                              </div>
                              <div className="mt-2.5 flex justify-center w-full">
                                <div className="p-1 bg-white">
                                  <QRCode value={walletAddress} size={128} />
                                </div>
                              </div>
                              <div className="flex flex-col items-center text-white mt-1">
                                <p className="text-center text-sm">
                                  Scan the QR code, or copy the wallet address to make a Crypto deposit.
                                </p>
                              </div>
                            </>
                          ) : <>
                          </>
                        }
                      </div>
                    </>
                  )
                }
              </>
            )
          }
        </>
      )
    },

  ]

  // Only show tabs that have corresponding list items
  const availableTabs = tabs.filter((tab) =>
    (tab.label === 'Fiat' && fiatPGList.length > 0) ||
    (tab.label === 'Crypto' && cryptoPGList.length > 0)
  );


  return (
    <>
      {isDemo ? (
        <div className="p-4 rounded-lg text-center">
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400 font-medium">
            For processing real deposits, please log in using an official account instead of a demo account.
          </p>
        </div>
      ) : (
        <div className="deposit-tabs">
          <div className="wallet-nav">
          <div className="nav nav-pills" role="tablist">
            {availableTabs.map((tab) => (
              <div className="nav-item" key={tab.label}>
                <a
                  role="tab"
                  className={`nav-link ${activeCurType === tab.label ? "active" : ""}`}
                  onClick={() => handleTabClick(tab.label)}
                >
                  {tab.label}
                </a>
              </div>
            ))}
          </div>

          {/* PG List display below tab label */}
          {!showPayment && activeCurType === "Fiat" && fiatPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {fiatPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? "active" : ""}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}

          {activeCurType === "Crypto" && cryptoPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {cryptoPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? "active" : ""}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}

          <div className="tab-content">
            {availableTabs.map((tab) => (
              <div
                key={tab.label}
                className={`tab-pane fade ${activeCurType === tab.label ? "show active" : ""
                  }`}
                id={`tabs-tabpane-${tab.label}`}
                role="tabpanel"
              >
                {activeCurType === tab.label && tab.content}
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
    siteConfig: state.userDetails?.siteConfig,
  };
};


export default connect(mapStateToProps)(DepositForm);
